import AgendaView from './AgendaView';
import DayView from './DayView';
import MonthView from './MonthView';
import { CalendarViewType } from '../schema';
import { observer } from 'mobx-react-lite';
import { assertNever } from 'owa-assert';
import * as React from 'react';
import { CalendarEventContextMenu } from 'owa-calendar-event-context-menu';
import { usePeekCalloutState } from '../hooks/usePeekCalloutState';
import { useContextMenuState } from '../hooks/useContextMenuState';
import { NUM_DAYS_WEEK_VIEW } from '../utils/calendarViewTypeUtils';
import type { PerformanceDatapoint } from 'owa-analytics';

export default observer(function CalendarEventsView(props: {
    viewType: CalendarViewType;
    scenarioId: string;
    combinedCalendarIds: string[];
    selectedDate: Date;
    onDateUpdated: (date: Date) => void;
    numDaysInDayView: number | undefined;
    assetPerfDatapoint: React.RefObject<PerformanceDatapoint | undefined>;
}) {
    const {
        scenarioId,
        combinedCalendarIds,
        viewType,
        onDateUpdated,
        selectedDate,
        numDaysInDayView,
        assetPerfDatapoint,
    } = props;

    const {
        selectedItemId,
        peekTarget,
        setPeekTarget,
        onClickCalendarEvent,
        onEventsPeekCalloutDismissed,
    } = usePeekCalloutState(viewType);

    const {
        onOpenEventDetails,
        onEditEvent,
        onCloseContextMenu,
        onContextMenuInvoked,
        contextMenuOpen,
        contextMenuViewState,
    } = useContextMenuState(setPeekTarget);

    const calendarViewBaseProps = {
        onOpenEventDetails,
        onContextMenuInvoked,
        contextMenuOpen,
        setPeekTarget,
        onClickCalendarEvent,
        selectedItemId,
        peekTarget,
        onEventsPeekCalloutDismissed,
    };

    let content: JSX.Element;
    switch (viewType) {
        case CalendarViewType.Agenda:
            content = (
                <AgendaView
                    scenarioId={scenarioId}
                    selectedDate={selectedDate}
                    onDateUpdated={onDateUpdated}
                    {...calendarViewBaseProps}
                />
            );
            break;
        case CalendarViewType.Day:
        case CalendarViewType.Week:
        case CalendarViewType.WorkWeek:
            content = (
                <DayView
                    scenarioId={scenarioId}
                    selectedDate={selectedDate}
                    onDateUpdated={onDateUpdated}
                    numDaysInDayView={
                        viewType == CalendarViewType.Day ? numDaysInDayView : NUM_DAYS_WEEK_VIEW
                    }
                    selectedViewType={viewType}
                    {...calendarViewBaseProps}
                    assetPerfDatapoint={assetPerfDatapoint}
                />
            );
            break;
        case CalendarViewType.Month:
            content = (
                <MonthView
                    calendarIds={combinedCalendarIds}
                    selectedDate={selectedDate}
                    onDateUpdated={onDateUpdated}
                    {...calendarViewBaseProps}
                />
            );
            break;
        default:
            assertNever(viewType);
    }

    return (
        <>
            {content}
            {contextMenuOpen && (
                <CalendarEventContextMenu
                    sourceId={'SpacesCalendarAssetContextMenu'}
                    viewState={contextMenuViewState}
                    onCloseContextMenu={onCloseContextMenu}
                    onOpenEventDetails={onOpenEventDetails}
                    onEditEvent={onEditEvent}
                    disableDeleteConfirmationDialog={true}
                />
            )}
        </>
    );
});
